import React, { useMemo } from 'react';
import { ClipboardList, Calendar, Clock } from 'lucide-react';

const ActivityFeed = ({ tasks, events, getUserName }) => {

    const feedItems = useMemo(() => {
        const items = [];

        // Process Tasks
        tasks.forEach(task => {
            items.push({
                id: `task-${task.TaskID}`,
                type: 'task',
                title: task.Title,
                status: task.Status,
                time: new Date(task.UpdatedAt || task.CreatedAt),
                user: getUserName ? getUserName(task.AssigneeID) : task.AssigneeID,
                division: task.Division
            });
        });

        // Process Events
        events.forEach(event => {
            items.push({
                id: `event-${event.EventID}`,
                type: 'event',
                title: event.Title,
                status: 'Scheduled',
                time: new Date(event.StartDateTime),
                user: 'System', // Events usually don't have a single "actor" in this context
                division: event.Division
            });
        });

        // Sort descending
        return items.sort((a, b) => b.time - a.time).slice(0, 20); // Top 20 recent
    }, [tasks, events, getUserName]);

    const getTimeAgo = (date) => {
        const seconds = Math.floor((new Date() - date) / 1000);
        let interval = seconds / 31536000;
        if (interval > 1) return Math.floor(interval) + "y ago";
        interval = seconds / 2592000;
        if (interval > 1) return Math.floor(interval) + "mo ago";
        interval = seconds / 86400;
        if (interval > 1) return Math.floor(interval) + "d ago";
        interval = seconds / 3600;
        if (interval > 1) return Math.floor(interval) + "h ago";
        interval = seconds / 60;
        if (interval > 1) return Math.floor(interval) + "m ago";
        return Math.floor(seconds) + "s ago";
    };

    return (
        <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #ddd', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1rem', borderBottom: '1px solid #eee' }}>
                <h4 style={{ margin: 0, color: '#5f6368', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Clock size={16} /> Latest Activity
                </h4>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 1rem', maxHeight: '600px' }}>
                {feedItems.length === 0 ? (
                    <div style={{ padding: '1rem', textAlign: 'center', color: '#999' }}>No recent activity</div>
                ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {feedItems.map((item, index) => (
                            <li key={item.id} style={{
                                padding: '1rem 0',
                                borderBottom: index < feedItems.length - 1 ? '1px solid #f1f3f4' : 'none',
                                display: 'flex', gap: '0.75rem', alignItems: 'flex-start'
                            }}>
                                <div style={{
                                    padding: '6px', borderRadius: '50%',
                                    background: item.type === 'task' ? '#e8f0fe' : '#fce8e6',
                                    color: item.type === 'task' ? '#1967d2' : '#d93025',
                                    marginTop: '2px'
                                }}>
                                    {item.type === 'task' ? <ClipboardList size={14} /> : <Calendar size={14} />}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#202124', marginBottom: '2px' }}>
                                        {item.title}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#5f6368', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>{item.type === 'task' ? 'Task' : 'Event'}</span>
                                        <span>•</span>
                                        <span>{item.user}</span>
                                        <span>•</span>
                                        <span style={{
                                            background: '#f1f3f4', padding: '1px 6px', borderRadius: '4px', fontSize: '0.75rem'
                                        }}>
                                            {item.status}
                                        </span>
                                    </div>
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#80868b', whiteSpace: 'nowrap' }}>
                                    {getTimeAgo(item.time)}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default ActivityFeed;
